import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft } from "lucide-react";
import { BriefHeader } from "@/components/BriefHeader";
import { ChatPane } from "@/components/ChatPane";
import { LabelControls, ReviewPane } from "@/components/ReviewPane";
import { SectionPanel } from "@/components/SectionPanel";
import { SectionPicker } from "@/components/SectionPicker";
import { SectionRail } from "@/components/SectionRail";
import { SubmitDialog } from "@/components/SubmitDialog";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { useBrief, useBriefActions } from "@/lib/briefs";

/**
 * The writing screen (SPEC §3). Left: the sections. Right: the box being
 * written and, below it, the interview — the one place to answer. The box
 * follows the AI: when it writes one, that box comes up; picking a box in the
 * rail pins it and focuses the interview on it. On a phone the rail is a picker
 * across the top and the page scrolls, with the composer kept in reach.
 */
export function Editor() {
  const { id = "" } = useParams();
  const detail = useBrief(id);
  const actions = useBriefActions(id);
  const [pinnedId, setPinnedId] = useState<string | null>(null);
  const [lastWrittenId, setLastWrittenId] = useState<string | null>(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newQuestion, setNewQuestion] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);

  // Opening the brief clears "new update".
  useEffect(() => {
    if (detail.data?.brief.ownerUnseen && detail.data.access.as === "owner") actions.seen.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.data?.brief.ownerUnseen, detail.data?.access.as]);

  if (detail.isPending) {
    return <main className="text-muted-foreground p-8 text-sm">Loading…</main>;
  }
  if (detail.isError || !detail.data) {
    return (
      <main className="mx-auto max-w-md p-8 text-center">
        <p className="font-medium">That brief isn't here.</p>
        <p className="text-muted-foreground mt-1 text-sm">It may have been deleted, or it isn't yours to see.</p>
        <Button className="mt-4" variant="outline" onClick={() => window.location.assign("/")}>
          <ArrowLeft size={14} /> My briefs
        </Button>
      </main>
    );
  }

  const d = detail.data;
  const shownId = pinnedId ?? lastWrittenId;
  const shown = d.sections.find((s) => s.id === shownId) ?? null;
  const pinned = d.sections.find((s) => s.id === pinnedId) ?? null;
  const onPick = (sid: string | null) => {
    setPinnedId(sid);
    if (sid === null) setLastWrittenId(null);
  };
  // A chip in the chat opens its box, as the rail does, and brings it into view
  // (on a phone the box may be far above the chat).
  const onOpenSection = (sid: string) => {
    onPick(sid);
    requestAnimationFrame(() => panelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }));
  };
  const loose = d.attachments.filter((a) => a.sectionId === null || !d.sections.some((s) => s.id === a.sectionId));
  const canEdit = d.access.canEdit;


  return (
    <main className="mx-auto max-w-[1440px] lg:grid lg:h-[calc(100dvh-3.5rem)] lg:grid-cols-[256px_minmax(0,1fr)]">
      <aside className="border-border hidden overflow-y-auto border-r px-3 py-4 lg:block">
        <Link to="/" className="text-muted-foreground mb-3 block px-2.5 text-xs hover:underline">
          ← My briefs
        </Link>
        <SectionRail
          briefId={d.brief.id}
          sections={d.sections}
          looseAttachments={loose}
          shownId={shown?.id ?? null}
          onPick={onPick}
          onAdd={() => setAddOpen(true)}
          canEdit={canEdit}
        />
      </aside>

      <div className="flex min-h-0 flex-col lg:h-full">
        <BriefHeader detail={d} onSubmit={() => setSubmitOpen(true)} />
        <div className="px-4 pt-3 lg:hidden">
          <SectionPicker sections={d.sections} shownId={shown?.id ?? null} onPick={onPick} onAdd={() => setAddOpen(true)} canEdit={canEdit} />
        </div>
        <div ref={panelRef} className="scroll-mt-16 px-4 pt-3 lg:px-6">
          <SectionPanel briefId={d.brief.id} sections={d.sections} attachments={d.attachments} shown={shown} pinned={pinned !== null} canEdit={canEdit} onPick={onPick} />
        </div>
        <div className="min-h-0 flex-1 pt-2 lg:overflow-hidden">
          {d.access.as === "owner" ? (
            <div className="flex h-full flex-col">
              {/* An admin's own submitted brief: they review it from here, not from a separate view. */}
              {d.access.canLabel ? (
                <details className="border-border mx-4 mb-2 rounded-md border px-3 py-2 text-sm lg:mx-6">
                  <summary className="cursor-pointer font-medium">
                    Label and remark{d.brief.adminLabel ? ` · ${d.brief.adminLabel}` : ""}
                  </summary>
                  <div className="pt-2">
                    <LabelControls detail={d} />
                  </div>
                </details>
              ) : null}
              <div className="min-h-0 flex-1">
                <ChatPane detail={d} focus={pinned} onClearFocus={() => setPinnedId(null)} onBriefWrite={(ids) => setLastWrittenId(ids[ids.length - 1] ?? null)}
                  onOpenSection={onOpenSection}
                />
              </div>
            </div>
          ) : (
            <ReviewPane detail={d} />
          )}
        </div>
      </div>

      <SubmitDialog detail={d} open={submitOpen} onClose={() => setSubmitOpen(false)} />

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} title="Add a section">
        <form
          className="space-y-3 text-sm"
          onSubmit={(e) => {
            e.preventDefault();
            const title = newTitle.trim();
            if (!title) return;
            actions.addSection.mutate(
              { title, guidingQuestion: newQuestion.trim() || null },
              {
                onSuccess: ({ section }) => {
                  setAddOpen(false);
                  setNewTitle("");
                  setNewQuestion("");
                  setPinnedId(section.id);
                },
              },
            );
          }}
        >
          <p className="text-muted-foreground">The six boxes stay. Add anything else we should know: the sites involved, a deadline, who else to talk to.</p>
          <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} maxLength={80} placeholder="Section title" autoFocus required />
          <Input value={newQuestion} onChange={(e) => setNewQuestion(e.target.value)} maxLength={200} placeholder="A guiding question (optional)" />
          {actions.addSection.isError ? <p className="text-destructive">{(actions.addSection.error as Error).message}</p> : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={actions.addSection.isPending}>
              Add
            </Button>
          </div>
        </form>
      </Dialog>
    </main>
  );
}
